import { AdminTableContainer, Badge, Button, cn, EmptyState } from "@simplicity/ui";
import { formatAbsoluteTime, formatRelativeTime } from "../../lib/formatRelativeTime";
import { AgentBadge, PausedBadge, WorkspaceStatusBadge } from "./OnsiteBadges";
import { missingPermissions, sftpIp } from "./onsiteModel";
import type { OnsiteWorkspace } from "./types";

function LastSeen({ at }: { at: string | null }) {
  if (!at) return <span className="col-muted">never</span>;
  const ms = Date.parse(at);
  return (
    <span className="col-muted" title={formatAbsoluteTime(ms)}>
      {formatRelativeTime(ms)}
    </span>
  );
}

function MissingPermissions({ workspace }: { workspace: OnsiteWorkspace }) {
  const missing = missingPermissions(workspace);
  if (missing === null) return <span className="col-muted">no self-check yet</span>;
  if (missing.length === 0) return <span className="col-muted">none</span>;
  return (
    <div className="space-y-1">
      <Badge variant="destructive">{missing.length} missing</Badge>
      <ul className="text-xs text-destructive">
        {missing.map((permission) => (
          <li key={permission}>{permission}</li>
        ))}
      </ul>
    </div>
  );
}

function WorkspaceRow({
  workspace,
  selected,
  onSelect,
}: {
  workspace: OnsiteWorkspace;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const ip = sftpIp(workspace);
  return (
    <tr className={cn(selected && "bg-muted")} aria-current={selected ? "true" : undefined}>
      <td>
        <Button variant="link" className="h-auto px-0" onClick={() => onSelect(workspace.id)}>
          {workspace.name}
        </Button>
      </td>
      <td>{workspace.projectName}</td>
      <td className="col-muted">{workspace.gcpProjectId}</td>
      <td>
        <div className="flex flex-wrap gap-1">
          <WorkspaceStatusBadge status={workspace.status} />
          <PausedBadge workspace={workspace} />
        </div>
      </td>
      <td>
        <AgentBadge agent={workspace.agent} />
      </td>
      <td>
        <LastSeen at={workspace.lastSeenAt} />
      </td>
      <td>
        <MissingPermissions workspace={workspace} />
      </td>
      <td>{ip ?? <span className="col-muted">—</span>}</td>
    </tr>
  );
}

export function WorkspacesTable({
  workspaces,
  selectedId,
  onSelect,
}: {
  workspaces: OnsiteWorkspace[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (workspaces.length === 0) {
    return (
      <div className="admin-table-container">
        <EmptyState
          title="No onsite workspaces yet"
          description="Register one per client GCP project."
        />
      </div>
    );
  }
  return (
    <AdminTableContainer aria-label="Onsite workspaces">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Runner project</th>
            <th>GCP project</th>
            <th>Status</th>
            <th>Agent image</th>
            <th>Agent last seen</th>
            <th>Missing permissions</th>
            <th>SFTP IP</th>
          </tr>
        </thead>
        <tbody>
          {workspaces.map((workspace) => (
            <WorkspaceRow
              key={workspace.id}
              workspace={workspace}
              selected={workspace.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </tbody>
      </table>
    </AdminTableContainer>
  );
}
